import { keepPreviousData, useMutation, useQuery, type QueryKey } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { BIOMETRIA_NAO_CADASTRADA } from '@/features/biometria/types/biometria'
import { codigoDoErro, normalizeApiError } from '@/lib/apiEnvelope'
import { saveBlob } from '@/lib/download'
import { queryClient } from '@/lib/queryClient'

import { visitasApi } from '../api/visitasApi'
import type {
  CalendarioArquivoPayload,
  RelatorioFilters,
  SugestaoFilters,
  Visita,
  VisitaCreatePayload,
  VisitaDetalhe,
  VisitaExclusaoPayload,
  VisitaListFilters,
} from '../types/visita'
import { operacionalKeys } from './operacionalKeys'

/** Invalida o módulo inteiro menos os detalhes de visita — listas, calendário, painel. */
const invalidarForaDoDetalhe = () =>
  queryClient.invalidateQueries({
    queryKey: operacionalKeys.all,
    predicate: (query) => query.queryKey[2] !== 'detail',
  })

/**
 * Depois de uma ação sobre a visita: grava o detalhe devolvido pela API no cache
 * (sem refetch) e invalida o restante do módulo.
 */
export function aplicarDetalhe(detailKey: QueryKey, visita: VisitaDetalhe) {
  queryClient.setQueryData(detailKey, visita)
  return invalidarForaDoDetalhe()
}

/** O detalhe da visita aparece tanto no planejamento quanto na revisão. */
const aplicarNoPlanejamento = (visita: VisitaDetalhe) =>
  aplicarDetalhe(operacionalKeys.visitas.detail(visita.id), visita)

export function useVisitas(filters: VisitaListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: operacionalKeys.visitas.list(filters),
    queryFn: () => visitasApi.list(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  })
}

const PAGE_SIZE_PERIODO = 100
/** Trava de segurança: um mês nunca deveria passar disso. */
const MAX_PAGINAS = 20

/** Todas as visitas do período (o calendário precisa do mês inteiro, não de uma página). */
export function useVisitasDoPeriodo(filters: Omit<VisitaListFilters, 'page' | 'pageSize'>) {
  return useQuery({
    queryKey: operacionalKeys.visitas.periodo(filters),
    queryFn: async (): Promise<Visita[]> => {
      const visitas: Visita[] = []
      for (let page = 1; page <= MAX_PAGINAS; page += 1) {
        const pagina = await visitasApi.list({ ...filters, page, pageSize: PAGE_SIZE_PERIODO })
        visitas.push(...pagina.items)
        if (page >= pagina.totalPages) break
      }
      return visitas
    },
    placeholderData: keepPreviousData,
  })
}

export function useSugestoes(filters: SugestaoFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: operacionalKeys.visitas.sugestoes(filters),
    queryFn: () => visitasApi.sugestoes(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  })
}

/** Aprovar e reabrir invalidam esta lista junto com o restante do módulo (`aplicarDetalhe`). */
export function useRelatoriosAprovados(filters: RelatorioFilters) {
  return useQuery({
    queryKey: operacionalKeys.visitas.relatorios(filters),
    queryFn: () => visitasApi.relatorios(filters),
    placeholderData: keepPreviousData,
  })
}

export function useVisita(id: string | undefined) {
  return useQuery({
    queryKey: operacionalKeys.visitas.detail(id ?? ''),
    queryFn: () => visitasApi.getById(id as string),
    enabled: !!id,
  })
}

export function useVisitaHistorico(id: string | undefined) {
  return useQuery({
    queryKey: operacionalKeys.visitas.historico(id ?? ''),
    queryFn: () => visitasApi.historico(id as string),
    enabled: !!id,
  })
}

/** Sem toast de erro: o formulário/painel mostra a mensagem. */
export function useCreateVisita() {
  return useMutation({
    mutationFn: (payload: VisitaCreatePayload) => visitasApi.create(payload),
    onSuccess: (visita) => void aplicarNoPlanejamento(visita),
  })
}

const periodoKey = () => [...operacionalKeys.visitas.all(), 'periodo'] as const

/**
 * Otimista: a visita muda de dia no calendário antes da resposta (arrastar e soltar) e
 * volta se a API recusar. Sem toast: quem chama decide a mensagem (o calendário oferece
 * "Desfazer"). O erro fica com quem chama.
 */
export function useRemarcarVisita() {
  return useMutation({
    mutationFn: ({ id, dataPrevista }: { id: string; dataPrevista: string }) =>
      visitasApi.remarcar(id, dataPrevista),
    onMutate: async ({ id, dataPrevista }) => {
      await queryClient.cancelQueries({ queryKey: periodoKey() })
      const anteriores = queryClient.getQueriesData<Visita[]>({ queryKey: periodoKey() })
      queryClient.setQueriesData<Visita[]>({ queryKey: periodoKey() }, (lista) =>
        lista?.map((visita) => (visita.id === id ? { ...visita, dataPrevista } : visita)),
      )
      return { anteriores }
    },
    onError: (_error, _variables, contexto) => {
      for (const [key, data] of contexto?.anteriores ?? []) queryClient.setQueryData(key, data)
      void queryClient.invalidateQueries({ queryKey: periodoKey() })
    },
    onSuccess: (visita) => void aplicarNoPlanejamento(visita),
  })
}

/** O `MotivoDialog` exibe o erro da API. */
export function useCancelarVisita() {
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => visitasApi.cancelar(id, motivo),
    onSuccess: (visita) => {
      void aplicarNoPlanejamento(visita)
      toast.success('Visita cancelada.')
    },
  })
}

/** O `MotivoDialog` exibe o erro da API (409 quando a visita deixou de estar APROVADA). */
export function useReabrirVisita() {
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => visitasApi.reabrir(id, motivo),
    onSuccess: (visita) => {
      void aplicarNoPlanejamento(visita)
      toast.success('Visita reaberta. Ela voltou para a fila de revisão.')
    },
  })
}

/**
 * O `ExcluirVisitaDialog` exibe o erro da API. A visita deixa de existir: detalhe e histórico dela só
 * são marcados como obsoletos, sem refetch (o detalhe segue montado até a navegação concluir, e
 * refazê-los daria 404). O resto do módulo recarrega — calendário, listas, painel e sugestões (a
 * unidade pode voltar a ser sugerida). No 409 (alterada por outra pessoa), recarrega tudo.
 */
export function useExcluirVisita() {
  return useMutation({
    mutationFn: ({ id, ...payload }: VisitaExclusaoPayload & { id: string }) =>
      visitasApi.excluir(id, payload),
    onSuccess: (_data, { id }) => {
      for (const queryKey of [
        operacionalKeys.visitas.detail(id),
        operacionalKeys.visitas.historico(id),
      ]) {
        void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
      }
      void queryClient.invalidateQueries({
        queryKey: operacionalKeys.all,
        predicate: (query) => query.queryKey[2] !== 'detail' && query.queryKey[3] !== id,
      })
      toast.success('Visita excluída.')
    },
    onError: (error) => {
      // O 409 sem aparelho cadastrado não é conflito de dados: nada a recarregar.
      const conflito =
        normalizeApiError(error).status === 409 && codigoDoErro(error) !== BIOMETRIA_NAO_CADASTRADA
      if (conflito) void queryClient.invalidateQueries({ queryKey: operacionalKeys.all })
    },
  })
}

/** Baixa os calendários do mês (PDF/PNG, ou `.zip` com vários). O erro fica com o modal. */
export function useExportarCalendario() {
  return useMutation({
    mutationFn: (payload: CalendarioArquivoPayload) => visitasApi.calendarioArquivo(payload),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
  })
}
